import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { BadgeShowcase } from "../components/BadgeDisplay";
import { 
  GraduationCap, 
  Clock,
  CheckCircle2,
  ChevronRight,
  BookOpen,
  Play,
  Calculator,
  Shield,
  TrendingDown,
  FileText,
  Target,
  Award,
  Trophy
} from "lucide-react";
import { toast } from "sonner";

const DIFFICULTY_COLORS = {
  beginner: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  intermediate: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  advanced: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const COURSE_ICONS = {
  calculator: Calculator,
  shield: Shield,
  "trending-down": TrendingDown,
  "file-text": FileText,
  target: Target,
  award: Award,
  book: BookOpen,
};

export const CoursesPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [badges, setBadges] = useState({ earned: [], available: [] });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesRes, badgesRes] = await Promise.all([
        api.get("/courses"),
        api.get("/user/badges")
      ]);
      setCourses(coursesRes.data.courses || []);
      setBadges(badgesRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const completedCount = courses.filter(c => c.is_completed).length;
  const totalLessonsCompleted = courses.reduce((sum, c) => sum + (c.completed_lessons || 0), 0);
  const totalLessons = courses.reduce((sum, c) => sum + (c.lessons_count || 0), 0);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="courses-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-64 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="courses-page">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <GraduationCap className="w-7 h-7 text-primary" />
            Learn
          </h1>
          <p className="text-muted-foreground mt-1">
            Master your finances with bite-sized lessons
          </p>
        </div>
        
        {/* Stats Badge */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted">
            <Trophy className="w-5 h-5 text-amber-500" />
            <span className="text-sm font-medium">
              {completedCount}/{courses.length} courses completed
            </span>
          </div>
        </div>
      </header>

      {/* Overall Progress */}
      {totalLessons > 0 && (
        <Card className="border-border/50 bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Your Learning Journey</h3>
                  <p className="text-sm text-muted-foreground">
                    {totalLessonsCompleted} of {totalLessons} lessons completed
                  </p>
                </div>
              </div>
              <span className="text-2xl font-bold text-primary">
                {Math.round((totalLessonsCompleted / totalLessons) * 100)}%
              </span>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${(totalLessonsCompleted / totalLessons) * 100}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Badges Section */}
      <Card className="border-border/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Your Badges
            </h3>
            <span className="text-sm text-muted-foreground">
              {badges.total_earned || 0} of {badges.total_available || 12} earned
            </span>
          </div>
          <BadgeShowcase 
            earnedBadges={badges.earned || []} 
            availableBadges={badges.available || []}
          />
        </CardContent>
      </Card>

      {/* Course Grid */}
      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {courses.map((course) => {
          const Icon = COURSE_ICONS[course.icon] || BookOpen;
          const progress = course.lessons_count > 0 
            ? (course.completed_lessons / course.lessons_count) * 100 
            : 0;
          
          return (
            <Card 
              key={course.id} 
              className={`border-border/50 hover:shadow-md transition-all cursor-pointer group ${
                course.is_completed ? "bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800" : ""
              }`}
              onClick={() => navigate(`/courses/${course.id}`)}
              data-testid={`course-card-${course.id}`}
            >
              <CardContent className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    course.is_completed 
                      ? "bg-emerald-100 dark:bg-emerald-900/30" 
                      : "bg-primary/10"
                  }`}>
                    {course.is_completed ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    ) : (
                      <Icon className="w-6 h-6 text-primary" />
                    )}
                  </div>
                  <Badge variant="secondary" className={DIFFICULTY_COLORS[course.difficulty]}>
                    {course.difficulty}
                  </Badge>
                </div>

                {/* Title & Description */}
                <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                  {course.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                  {course.description}
                </p>

                {/* Topics */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {course.topics.slice(0, 3).map((topic, idx) => (
                    <span 
                      key={idx}
                      className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground"
                    >
                      {topic}
                    </span>
                  ))}
                </div>

                {/* Progress Bar */}
                {course.lessons_count > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                      <span>{course.completed_lessons}/{course.lessons_count} lessons</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          course.is_completed ? "bg-emerald-500" : "bg-primary"
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-border/50">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {course.duration}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="gap-1 text-primary hover:text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/courses/${course.id}`);
                    }}
                  >
                    {course.is_completed ? (
                      <>Review</>
                    ) : progress > 0 ? (
                      <>Continue</>
                    ) : (
                      <>
                        <Play className="w-3.5 h-3.5" />
                        Start
                      </>
                    )}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>

      {/* Empty State */}
      {courses.length === 0 && (
        <Card className="border-border/50">
          <CardContent className="py-16 text-center">
            <GraduationCap className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No courses available</h3>
            <p className="text-muted-foreground">
              Check back soon for new financial education content.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
